import {
  Container,
  Box,
  Flex,
  Text,
  VStack,
  Icon,
  Divider,
  HStack,
  SimpleGrid,
} from "@chakra-ui/react";
import { motion } from "framer-motion";
import { 
  Info, 
  Heart, 
  Code, 
  Sparkles,
  Truck,
  Calendar,
  Bell,
  CheckCircle2,
  Zap,
  Shield,
  Globe,
  Wind,
  Thermometer,
  Droplets
} from "lucide-react";
import styled, { useTheme } from "styled-components";
import { Copyright, BuyCoffeeButton } from "../../components";
import { AppTheme } from "../../theme";
import { THEME } from "../../helpers/constans";
import packageJson from "../../../package.json";
import dayjs from "dayjs";

const HeaderContainer = styled.header<{ $theme: AppTheme }>`
  background: ${({ $theme }) => $theme.gradientHeader};
  padding: 3.5rem 1rem;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: -50%;
    right: -20%;
    width: 500px;
    height: 500px;
    border-radius: 50%;
    background: ${({ $theme }) => `${$theme.primary}20`};
    filter: blur(60px);
  }
  
  &::after {
    content: '';
    position: absolute;
    bottom: -30%;
    left: -15%;
    width: 400px;
    height: 400px;
    border-radius: 50%;
    background: ${({ $theme }) => `${$theme.accent}15`};
    filter: blur(50px);
  }
`;

const MotionBox = motion(Box);

const FeatureCard = ({ icon, title, description, theme, delay = 0 }: { 
  icon: any; 
  title: string; 
  description: string; 
  theme: AppTheme;
  delay?: number;
}) => (
  <MotionBox
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.4, delay }}
    bg={theme.backgroundCard}
    borderRadius={THEME.borderRadiusLg}
    p={6}
    border={`1px solid ${theme.borderCard}`}
    boxShadow={theme.shadowSm}
    h="100%"
    _hover={{
      boxShadow: theme.shadowMd,
      transform: "translateY(-4px)",
      borderColor: theme.primary + "40",
    }}
    style={{ transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)" }}
  >
    <Flex align="flex-start" gap={4} mb={3}>
      <Box
        p={3}
        borderRadius={THEME.borderRadius}
        bg={`${theme.primary}15`}
        color={theme.primary}
        flexShrink={0}
      >
        <Icon as={icon} boxSize={6} />
      </Box>
      <Box flex={1}>
        <Text
          fontFamily="'Outfit', sans-serif"
          fontSize="lg"
          fontWeight={700}
          color={theme.text}
          letterSpacing="-0.02em"
          mb={2}
        >
          {title}
        </Text>
        <Text
          color={theme.textSecondary}
          fontSize="sm"
          lineHeight="1.7"
          fontWeight={500}
        >
          {description}
        </Text>
      </Box>
    </Flex>
  </MotionBox>
);

const StatCard = ({ label, value, icon, theme, delay = 0 }: { 
  label: string; 
  value: string; 
  icon?: any;
  theme: AppTheme;
  delay?: number;
}) => (
  <MotionBox
    initial={{ opacity: 0, scale: 0.9 }}
    animate={{ opacity: 1, scale: 1 }}
    transition={{ duration: 0.3, delay }}
    bg={theme.backgroundCard}
    borderRadius={THEME.borderRadiusLg}
    p={5}
    textAlign="center"
    border={`1px solid ${theme.borderCard}`}
    boxShadow={theme.shadowSm}
    _hover={{
      boxShadow: theme.shadowMd,
      transform: "translateY(-2px)",
    }}
    style={{ transition: "all 0.3s ease" }}
  >
    {icon && (
      <Box
        display="inline-flex"
        p={2}
        borderRadius={THEME.borderRadius}
        bg={`${theme.primary}15`}
        color={theme.primary}
        mb={3}
      >
        <Icon as={icon} boxSize={5} />
      </Box>
    )}
    <Text
      fontFamily="'Outfit', sans-serif"
      fontSize="3xl"
      fontWeight={800}
      color={theme.primary}
      mb={1}
      lineHeight="1"
    >
      {value}
    </Text>
    <Text
      fontSize="xs"
      color={theme.textMuted}
      fontWeight={600}
      textTransform="uppercase"
      letterSpacing="0.1em"
    >
      {label}
    </Text>
  </MotionBox>
);

const SectionTitle = ({ children, theme }: { children: React.ReactNode; theme: AppTheme }) => (
  <Text
    fontFamily="'Outfit', sans-serif"
    fontSize="2xl"
    fontWeight={800}
    color={theme.text}
    mb={6}
    letterSpacing="-0.03em"
    position="relative"
    display="inline-block"
    _before={{
      content: '""',
      position: "absolute",
      bottom: "-8px",
      left: 0,
      width: "60px",
      height: "4px",
      background: theme.gradientPrimary,
      borderRadius: "2px",
    }}
  >
    {children}
  </Text>
);

export function AboutPage() {
  const theme = useTheme() as AppTheme;
  const currentYear = dayjs().format("YYYY");
  
  return (
    <Box minH="100vh" bg={theme.background}>
      <HeaderContainer $theme={theme}>
        <Container maxW="container.lg" position="relative" zIndex={1}>
          <MotionBox
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Flex align="center" gap={4} mb={4}>
              <Box
                p={3.5}
                borderRadius={THEME.borderRadiusLg}
                bg="rgba(255,255,255,0.15)"
                backdropFilter="blur(10px)"
                border="1px solid rgba(255,255,255,0.2)"
                boxShadow="0 8px 32px rgba(0,0,0,0.1)"
              >
                <Icon as={Info} boxSize={7} color="white" />
              </Box>
              <Box flex={1}>
                <HStack gap={2} mb={1}>
                  <Text
                    fontFamily="'Outfit', sans-serif"
                    fontSize="3xl"
                    fontWeight="900"
                    color="white"
                    letterSpacing="-0.03em"
                  >
                    O aplikacji
                  </Text>
                  <Icon as={Sparkles} boxSize={6} color="white" opacity={0.8} />
                </HStack>
                <Text color="whiteAlpha.900" fontSize="md" fontWeight="500">
                  Poznaj Śmieciarkę Jedzie
                </Text>
              </Box>
            </Flex>
          </MotionBox>
        </Container>
      </HeaderContainer>

      <Container maxW="container.lg" py={10} px={4}>
        <VStack spacing={10} align="stretch">
          {/* Statystyki */}
          <Box>
            <SectionTitle theme={theme}>Informacje</SectionTitle>
            <SimpleGrid columns={{ base: 1, sm: 3 }} spacing={4}>
              <StatCard 
                label="Wersja" 
                value={`v${packageJson.version}`}
                icon={Code}
                theme={theme}
                delay={0.1}
              />
              <StatCard 
                label="Rok" 
                value={currentYear}
                icon={Calendar}
                theme={theme}
                delay={0.15}
              />
              <StatCard 
                label="PWA" 
                value="✓"
                icon={CheckCircle2}
                theme={theme}
                delay={0.2}
              />
            </SimpleGrid>
          </Box>

          {/* Opis aplikacji */}
          <MotionBox
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.25 }}
            bg={theme.backgroundCard}
            borderRadius={THEME.borderRadiusXl}
            p={8}
            border={`1px solid ${theme.borderCard}`}
            boxShadow={theme.shadowMd}
          >
            <Flex align="center" gap={4} mb={6}>
              <Box
                p={3}
                borderRadius={THEME.borderRadiusLg}
                bg={`${theme.primary}15`}
                color={theme.primary}
              >
                <Icon as={Truck} boxSize={7} />
              </Box>
              <Box>
                <Text
                  fontFamily="'Outfit', sans-serif"
                  fontSize="2xl"
                  fontWeight={800}
                  color={theme.text}
                  letterSpacing="-0.02em"
                  mb={1}
                >
                  Śmieciarka Jedzie
                </Text>
                <Text
                  color={theme.textSecondary}
                  fontSize="sm"
                  fontWeight={500}
                >
                  Aplikacja dla mieszkańców gminy Pobiedziska
                </Text>
              </Box>
            </Flex>
            
            <Text
              color={theme.textSecondary}
              fontSize="md"
              lineHeight="1.8"
              fontWeight={500}
              mb={6}
            >
              Aplikacja pomaga mieszkańcom gminy Pobiedziska w śledzeniu harmonogramu wywozu odpadów. 
              Zawsze wiedz, kiedy wystawić kosze i jakie odpady będą odbierane. 
              Prosty interfejs i intuicyjna nawigacja sprawiają, że korzystanie z aplikacji jest przyjemnością.
            </Text>
            
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} mb={6}>
              <Box
                bg={`${theme.primary}10`}
                border={`1px solid ${theme.primary}30`}
                borderRadius={THEME.borderRadiusLg}
                p={5}
              >
                <HStack gap={3} mb={3}>
                  <Box
                    p={2}
                    borderRadius={THEME.borderRadius}
                    bg={`${theme.primary}20`}
                    color={theme.primary}
                  >
                    <Icon as={Zap} boxSize={5} />
                  </Box>
                  <Text
                    color={theme.text}
                    fontSize="md"
                    fontWeight={700}
                    fontFamily="'Outfit', sans-serif"
                  >
                    Niezależna aplikacja
                  </Text>
                </HStack>
                <Text
                  color={theme.textSecondary}
                  fontSize="sm"
                  lineHeight="1.7"
                  fontWeight={500}
                >
                  <strong>Śmieciarka Jedzie</strong> to całkowicie niezależna, niekomercyjna aplikacja stworzona przez programistę z pasją. 
                  Nie jest powiązana z żadną instytucją publiczną ani firmą komunalną.
                </Text>
              </Box>
              
              <Box
                bg={`${theme.accent}10`}
                border={`1px solid ${theme.accent}30`}
                borderRadius={THEME.borderRadiusLg}
                p={5}
              >
                <HStack gap={3} mb={3}>
                  <Box
                    p={2}
                    borderRadius={THEME.borderRadius}
                    bg={`${theme.accent}20`}
                    color={theme.accent}
                  >
                    <Icon as={Shield} boxSize={5} />
                  </Box>
                  <Text
                    color={theme.text}
                    fontSize="md"
                    fontWeight={700}
                    fontFamily="'Outfit', sans-serif"
                  >
                    Całkowicie darmowa
                  </Text>
                </HStack>
                <Text
                  color={theme.textSecondary}
                  fontSize="sm"
                  lineHeight="1.7"
                  fontWeight={500}
                >
                  Aplikacja jest <strong>w pełni darmowa</strong>. Tworzona z pasją, aby ułatwić życie mieszkańcom. 
                  Każda aktualizacja to dziesiątki godzin pracy, testów i szlifowania detali.
                </Text>
              </Box>
            </SimpleGrid>
            
            <Box
              bg={theme.backgroundMuted}
              border={`1px solid ${theme.border}`}
              borderRadius={THEME.borderRadiusLg}
              p={5}
            >
              <HStack gap={3} mb={2}>
                <Icon as={Globe} boxSize={5} color={theme.primary} />
                <Text
                  color={theme.text}
                  fontSize="sm"
                  fontWeight={600}
                >
                  Dane publiczne
                </Text>
              </HStack>
              <Text
                color={theme.textSecondary}
                fontSize="sm"
                lineHeight="1.7"
                fontWeight={500}
              >
                Aplikacja wykorzystuje publicznie dostępne dane, aby ułatwić mieszkańcom dostęp do informacji o harmonogramie wywozu odpadów.
              </Text>
            </Box>
          </MotionBox>

          {/* Funkcje */}
          <Box>
            <SectionTitle theme={theme}>Funkcje aplikacji</SectionTitle>
            <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={5}>
              <FeatureCard
                icon={Calendar}
                title="Harmonogram odbioru"
                description="Przeglądaj harmonogram wywozu odpadów na najbliższe tygodnie i miesiące. Zawsze bądź na bieżąco z terminami odbioru."
                theme={theme}
                delay={0.3}
              />
              <FeatureCard
                icon={Bell}
                title="Powiadomienia"
                description="Dodawaj wydarzenia do kalendarza i otrzymuj przypomnienia o odbiorze. Nigdy nie przegap terminu wywozu."
                theme={theme}
                delay={0.35}
              />
              <FeatureCard
                icon={Truck}
                title="Wizualizacja"
                description="Animowana śmieciarka pokazuje dni odbioru w przejrzysty sposób. Intuicyjny interfejs ułatwia nawigację."
                theme={theme}
                delay={0.4}
              />
              <FeatureCard
                icon={Wind}
                title="Jakość powietrza"
                description="Monitoruj aktualną jakość powietrza w czasie rzeczywistym. Sprawdzaj wskaźniki AQI, PM2.5 i PM10 dla lepszego zdrowia."
                theme={theme}
                delay={0.45}
              />
              <FeatureCard
                icon={Thermometer}
                title="Temperatura"
                description="Śledź aktualną temperaturę powietrza. Zawsze wiedz, jakie warunki panują na zewnątrz."
                theme={theme}
                delay={0.5}
              />
              <FeatureCard
                icon={Droplets}
                title="Wilgotność"
                description="Monitoruj poziom wilgotności powietrza. Poznaj warunki atmosferyczne wpływające na komfort."
                theme={theme}
                delay={0.55}
              />
            </SimpleGrid>
          </Box>

          <Divider borderColor={theme.border} borderWidth="1px" />

          {/* Autor */}
          <MotionBox
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.45 }}
            bg={theme.backgroundCard}
            borderRadius={THEME.borderRadiusXl}
            p={8}
            border={`1px solid ${theme.borderCard}`}
            boxShadow={theme.shadowMd}
          >
            <Flex direction="column" align="center" gap={6}>
              <Box textAlign="center" maxW="600px">
                <HStack justify="center" gap={2} mb={4}>
                  <Icon as={Heart} boxSize={6} color={theme.primary} fill={theme.primary} />
                  <Text
                    fontFamily="'Outfit', sans-serif"
                    fontSize="2xl"
                    fontWeight={800}
                    color={theme.text}
                    letterSpacing="-0.02em"
                  >
                    Stworzone przez Jana Osio
                  </Text>
                </HStack>
                <Text
                  color={theme.textSecondary}
                  fontSize="md"
                  fontWeight={500}
                  lineHeight="1.8"
                  mb={5}
                >
                  Jestem programistą, który postanowił stworzyć tę aplikację, aby pomóc mieszkańcom 
                  gminy Pobiedziska w łatwiejszym śledzeniu harmonogramu wywozu odpadów. 
                  Aplikacja powstała z potrzeby i pasji do tworzenia użytecznych rozwiązań.
                </Text>
                
                <Box
                  bg={theme.backgroundMuted}
                  borderRadius={THEME.borderRadiusLg}
                  p={4}
                  mb={6}
                >
                  <SimpleGrid columns={2} spacing={4} textAlign="left">
                    <Box>
                      <Text
                        fontSize="xs"
                        color={theme.textMuted}
                        fontWeight={600}
                        textTransform="uppercase"
                        letterSpacing="0.1em"
                        mb={1}
                      >
                        Wersja
                      </Text>
                      <Text
                        fontFamily="'Outfit', sans-serif"
                        fontSize="lg"
                        fontWeight={700}
                        color={theme.text}
                      >
                        {packageJson.version}
                      </Text>
                    </Box>
                    <Box>
                      <Text
                        fontSize="xs"
                        color={theme.textMuted}
                        fontWeight={600}
                        textTransform="uppercase"
                        letterSpacing="0.1em"
                        mb={1}
                      >
                        Status
                      </Text>
                      <Text
                        fontFamily="'Outfit', sans-serif"
                        fontSize="lg"
                        fontWeight={700}
                        color={theme.primary}
                      >
                        Aktywnie rozwijana
                      </Text>
                    </Box>
                  </SimpleGrid>
                </Box>
              </Box>
              
              <Box>
                <BuyCoffeeButton />
              </Box>
            </Flex>
          </MotionBox>

        </VStack>

        <Box mt={12} mb={6}>
          <Copyright />
        </Box>
      </Container>
    </Box>
  );
}

